import React from 'react';

import {altKey, metaKey} from '../../../../core/src/helpers/browser/modifierKeyPlatformHelpers';
import {isMacOS} from '../../../../core/src/helpers/environment/platformHelpers';
import {Trans} from '../../../../core/src/helpers/internationalization/react';

/*
 * Platform awareness.
 */

export function splitAndFixKeys(shortcut: string) {
  const keys = shortcut.split('+');
  return keys.map(fixKeysByPlatform);
}

export function fixKeysByPlatform(key: string) {
  const platformSpecificKey = platformSpecificKeys[key];
  return platformSpecificKey || key;
}

type PlatformSpecificKeys = {[name: string]: string | undefined};
const platformSpecificKeys: PlatformSpecificKeys = {
  mod: metaKey,
  meta: metaKey,
  alt: altKey,
};

/*
 * Localization map.
 */

function renderDeleteKey() {
  if (isMacOS()) {
    return <Trans id="key-forward-delete">forward delete</Trans>;
  }

  return <Trans id="key-del">delete</Trans>;
}

export const knownKeyWithSymbols: Array<keyof typeof knownKeyRenderers> = [
  'shift',
  'ctrl',
  'command',
  'option',
  'backspace',
  'tab',
  'enter',
  'esc',
  'pageup',
  'pagedown',
  'left',
  'up',
  'right',
  'down',
  'delete',
] as const;

export const knownKeyRenderers: KeyRenderers = {
  // ⇧
  shift: (shouldUseSymbol) =>
    shouldUseSymbol ? <Trans id="key-shift-short">&#8679;</Trans> : <Trans id="key-shift">shift</Trans>,
  // ⌃
  ctrl: (shouldUseSymbol) =>
    shouldUseSymbol ? <Trans id="key-ctrl-short">&#8963;</Trans> : <Trans id="key-ctrl">ctrl</Trans>,
  command: (shouldUseSymbol) =>
    // ⌘
    shouldUseSymbol ? <Trans id="key-command-short">&#8984;</Trans> : <Trans id="key-command">cmd</Trans>,
  alt: () => <Trans id="key-alt">alt</Trans>,
  option: (shouldUseSymbol) =>
    // ⌥
    shouldUseSymbol ? <Trans id="key-option-short">&#8997;</Trans> : <Trans id="key-option">option</Trans>,
  backspace: (shouldUseSymbol) =>
    // ⌫
    shouldUseSymbol ? (
      <Trans id="key-backspace-short">&#9003;</Trans>
    ) : (
      <Trans id="key-backspace">backspace</Trans>
    ),
  tab: (shouldUseSymbol) =>
    // ⇥
    shouldUseSymbol ? <Trans id="key-tab-short">&#8677;</Trans> : <Trans id="key-tab">tab</Trans>,
  enter: (shouldUseSymbol) =>
    // ⏎
    shouldUseSymbol ? <Trans id="key-enter-short">&#9166;</Trans> : <Trans id="key-enter">enter</Trans>,
  esc: (shouldUseSymbol) =>
    // ⎋
    shouldUseSymbol ? <Trans id="key-esc-short">&#9099;</Trans> : <Trans id="key-esc">escape</Trans>,
  space: () => <Trans id="key-space">space</Trans>,

  // ⇞
  pageup: (shouldUseSymbol) =>
    shouldUseSymbol ? <Trans id="key-pageup-short">&#8670;</Trans> : <Trans id="key-pageup">page up</Trans>,
  pagedown: (shouldUseSymbol) =>
    // ⇟
    shouldUseSymbol ? (
      <Trans id="key-pagedown-short">&#8671;</Trans>
    ) : (
      <Trans id="key-pagedown">page down</Trans>
    ),
  end: () => <Trans id="key-end">end</Trans>,
  home: () => <Trans id="key-home">home</Trans>,
  left: (shouldUseSymbol) =>
    shouldUseSymbol ? <Trans id="key-left-short">&#8592;</Trans> : <Trans id="key-left">left</Trans>,
  up: (shouldUseSymbol) =>
    shouldUseSymbol ? <Trans id="key-up-short">&#8593;</Trans> : <Trans id="key-up">up</Trans>,
  right: (shouldUseSymbol) =>
    shouldUseSymbol ? <Trans id="key-right-short">&#8594;</Trans> : <Trans id="key-right">right</Trans>,
  down: (shouldUseSymbol) =>
    shouldUseSymbol ? <Trans id="key-down-short">&#8595;</Trans> : <Trans id="key-down">down</Trans>,
  ins: () => <Trans id="key-ins">insert</Trans>,
  del: renderDeleteKey,
} as const;

type KeyRenderers = {[name: string]: (shouldUseSymbol?: boolean) => React.ReactNode | undefined};
export const keyRenderers: KeyRenderers = knownKeyRenderers;
